import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaEnvelope, FaChevronDown, FaTrash, FaShieldAlt } from 'react-icons/fa';
import './SupportPage.css';
import { Header } from '../../components/header/Header';
import { MobileFooter } from '../../components/mobileFooter/MobileFooter';

const FaEnvelopeIcon = FaEnvelope as React.FC<React.SVGProps<SVGSVGElement>>;
const FaChevronDownIcon = FaChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaTrashIcon = FaTrash as React.FC<React.SVGProps<SVGSVGElement>>;
const FaShieldAltIcon = FaShieldAlt as React.FC<React.SVGProps<SVGSVGElement>>;

export const SupportPage = () => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: '-100px' });
	const [isAccordionOpen, setIsAccordionOpen] = useState(false);
	const [openAccordion, setOpenAccordion] = useState<string | null>(null);

	// Handle URL hash on mount
	useEffect(() => {
		const hash = window.location.hash.slice(1);
		if (hash) {
			setOpenAccordion(hash);
			setTimeout(() => {
				const element = document.getElementById(hash);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}, 100);
		}
	}, []);
	return (
		<div>
			<Header style={{ backgroundColor: '#ffffff' }} />
			<div className='support-page'>
				<div className='support-content-wrapper'>
					{/* Main Contact Card */}
					<section className='main-contact-section'>
						<motion.div
							ref={ref}
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut' }}
							className='main-contact-card'
						>
							<h2>Kontaktiere uns</h2>
							<p className='contact-description'>
								Hast du Fragen oder Probleme? Kontaktiere uns gerne – wir helfen dir
								schnell weiter!
							</p>
							<p className='contact-description'>
								Wenn du Fragen oder technische Probleme mit der Erledix App hast,
								kannst du uns gerne eine E-Mail schreiben.
							</p>
							<div className='contact-info-grid'>
								<motion.div
									className='contact-info-item'
									whileHover={{ y: -5 }}
									transition={{ duration: 0.3 }}
								>
									<FaEnvelopeIcon className='contact-info-icon' />
									<div className='contact-info-text'>
										<h3>E-Mail Support</h3>
										<a href='mailto:support@erledix.de'>support@erledix.de</a>
										<p className='contact-info-detail'>
											Wir antworten innerhalb von 24 Stunden
										</p>
									</div>
								</motion.div>
							</div>
						</motion.div>
					</section>

					{/* Registration Guide Section */}
					<section className='deletion-guide-section' id='registration'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
							className='deletion-guide-container'
						>
							<h2>Registrierung und erste Schritte</h2>

							<div className='guide-content'>
								<div className='guide-text'>
									<h3>Schritt-für-Schritt Anleitung</h3>
									<p>
										Folge dieser Anleitung, um dein Konto zu erstellen und die
										App optimal zu nutzen.
									</p>
								</div>

								<motion.button
									className='accordion-button'
									onClick={() =>
										setOpenAccordion(
											openAccordion === 'registration' ? null : 'registration'
										)
									}
									whileHover={{ backgroundColor: '#E8E6FF' }}
									whileTap={{ scale: 0.98 }}
								>
									<div className='accordion-header'>
										<div className='accordion-title'>
											<span>
												{openAccordion === 'registration'
													? 'Schritte ausblenden'
													: 'Schritte anzeigen'}
											</span>
										</div>
										<motion.div
											animate={{
												rotate: openAccordion === 'registration' ? 180 : 0,
											}}
											transition={{ duration: 0.3 }}
											className='chevron-icon'
										>
											<FaChevronDownIcon />
										</motion.div>
									</div>
								</motion.button>

								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{
										height: openAccordion === 'registration' ? 'auto' : 0,
										opacity: openAccordion === 'registration' ? 1 : 0,
									}}
									transition={{ duration: 0.3, ease: 'easeInOut' }}
									className='accordion-content'
								>
									<ol className='steps-list'>
										<li>
											<span className='step-number'>1</span>
											<div className='step-content'>
												<span className='step-title'>
													App herunterladen
												</span>
												<span className='step-description'>
													Lade die Erledix App aus dem App Store oder
													Google Play Store herunter.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>2</span>
											<div className='step-content'>
												<span className='step-title'>Konto erstellen</span>
												<span className='step-description'>
													Registriere dich mit deiner E-Mail-Adresse und
													erstelle ein sicheres Passwort.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>3</span>
											<div className='step-content'>
												<span className='step-title'>
													E-Mail bestätigen
												</span>
												<span className='step-description'>
													Bestätige deine E-Mail-Adresse über den Link in
													der Bestätigungsmail.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>4</span>
											<div className='step-content'>
												<span className='step-title'>
													Profil einrichten
												</span>
												<span className='step-description'>
													Füge dein Profilbild hinzu und personalisiere
													dein Elternprofil.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>5</span>
											<div className='step-content'>
												<span className='step-title'>
													Elternpin einrichten
												</span>
												<span className='step-description'>
													Erstelle eine 6-stellige PIN zum Schutz deines
													Elternprofils.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>6</span>
											<div className='step-content'>
												<span className='step-title'>Profilanmeldung</span>
												<span className='step-description'>
													Wähle dein Elternprofil aus und melde dich mit
													deiner Pin an. Genieße die App!
												</span>
											</div>
										</li>
									</ol>
								</motion.div>

								<div className='guide-additional-info'>
									<h3>Tipps zur Registrierung</h3>
									<ul>
										<li>
											<strong>Sicheres Passwort:</strong> Verwende ein starkes
											Passwort mit Groß- und Kleinbuchstaben, Zahlen und
											Sonderzeichen.
										</li>
										<li>
											<strong>E-Mail-Bestätigung:</strong> Überprüfe deinen
											Spam-Ordner, falls du die Bestätigungsmail nicht
											erhältst.
										</li>
										<li>
											<strong>Datenschutz:</strong> Deine Daten sind sicher
											und werden verschlüsselt gespeichert.
										</li>
									</ul>
								</div>
							</div>
						</motion.div>
					</section>

					{/* Child Management Guide Section */}
					<section className='deletion-guide-section' id='children'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.15 }}
							className='deletion-guide-container'
						>
							<h2>Kinderverwaltung</h2>

							{/* Kinderprofil anlegen */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Kinderprofil anlegen</h3>

										<p>
											{' '}
											Erstelle ein neues Kinderprofil für dein Kind mit Name,
											Icon und PIN.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'child-create'
													? null
													: 'child-create'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'child-create'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'child-create' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'child-create' ? 'auto' : 0,
											opacity: openAccordion === 'child-create' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Zur Profilübersicht navigieren
													</span>
													<span className='step-description'>
														Öffne die App und gehe zu deinen
														Profileinstellungen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Kinderprofil hinzufügen
													</span>
													<span className='step-description'>
														Klicke auf „Kinderprofil hinzufügen" oder
														das Plus-Symbol.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Name und Icon auswählen
													</span>
													<span className='step-description'>
														Gib den Namen des Kindes ein und wähle ein
														passendes Icon aus.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														PIN einrichten
													</span>
													<span className='step-description'>
														Erstelle eine 4-stellige PIN für das
														Kinderprofil zur Sicherheit.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>

							{/* Kinderprofil mit anderem Elternteil verknüpfen */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Kinderprofil mit anderem Elternteil verknüpfen</h3>
										<p>
											{' '}
											Verknüpfe ein Kinderprofil mit einem anderen Elternteil
											über einen Einladungscode.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'child-link' ? null : 'child-link'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'child-link'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'child-link' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'child-link' ? 'auto' : 0,
											opacity: openAccordion === 'child-link' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Im Erstellerprofil auf Profil klicken
													</span>
													<span className='step-description'>
														Öffne dein Elternprofil in den
														Einstellungen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Kind auswählen
													</span>
													<span className='step-description'>
														Wähle das Kinderprofil aus, das du
														verknüpfen möchtest.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Bei anderen Elternteil einbinden auswählen
													</span>
													<span className='step-description'>
														Klicke auf „Mit anderem Elternteil
														verknüpfen" oder ähnlich.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Einladungscode kopieren
													</span>
													<span className='step-description'>
														Kopiere den generierten Code und teile ihn
														mit dem anderen Elternteil.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>5</span>
												<div className='step-content'>
													<span className='step-title'>
														Anderer Elternteil: Code einfügen
													</span>
													<span className='step-description'>
														Der andere Elternteil geht zur
														Profilübersicht, klickt auf „Kind
														hinzufügen" und fügt den Code ein.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>6</span>
												<div className='step-content'>
													<span className='step-title'>Bestätigen</span>
													<span className='step-description'>
														Bestätige die Verknüpfung und das Kind ist
														nun mit beiden Elternteilen verbunden.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>

							{/* Kinderprofil auf eigenem Gerät freischalten */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Kinderprofil auf eigenem Gerät freischalten</h3>
										<p>
											{' '}
											Gib einem Kind Zugriff auf sein Profil auf einem eigenen
											Gerät.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'child-device'
													? null
													: 'child-device'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'child-device'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'child-device' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'child-device' ? 'auto' : 0,
											opacity: openAccordion === 'child-device' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Im Erstellerprofil auf Profil klicken
													</span>
													<span className='step-description'>
														Öffne dein Elternprofil in den
														Einstellungen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Kind auswählen
													</span>
													<span className='step-description'>
														Wähle das Kinderprofil aus, das du
														freischalten möchtest.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Gerät freischalten auswählen
													</span>
													<span className='step-description'>
														Klicke auf „Gerät freischalten" oder „Auf
														neuem Gerät aktivieren".
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Einladungscode kopieren
													</span>
													<span className='step-description'>
														Kopiere den generierten Code für das
														Zielgerät.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>5</span>
												<div className='step-content'>
													<span className='step-title'>
														App auf Zielgerät installieren
													</span>
													<span className='step-description'>
														Installiere die Erledix App auf dem Gerät
														des Kindes.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>6</span>
												<div className='step-content'>
													<span className='step-title'>
														„Ich bin ein Kind" auswählen
													</span>
													<span className='step-description'>
														Auf der Startseite wählst du „Ich bin ein
														Kind" statt „Ich bin ein Elternteil".
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>7</span>
												<div className='step-content'>
													<span className='step-title'>
														Code einfügen und bestätigen
													</span>
													<span className='step-description'>
														Gib den Code ein und bestätige. Das Profil
														ist nun auf diesem Gerät aktiv.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>
						</motion.div>
					</section>

					{/* Group Management Guide Section */}
					<section className='deletion-guide-section' id='groups'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
							className='deletion-guide-container'
						>
							<h2>Gruppenverwaltung</h2>

							{/* Gruppe erstellen */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Gruppe erstellen</h3>
										<p>
											Erstelle Gruppen, um Aufgaben und Belohnungen zu
											organisieren. Gruppen helfen dir, Aufgaben nach
											Kategorien zu sortieren und die Zusammenarbeit in der
											Familie zu verbessern.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'group-create'
													? null
													: 'group-create'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'group-create'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'group-create' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'group-create' ? 'auto' : 0,
											opacity: openAccordion === 'group-create' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Neue Gruppe erstellen
													</span>
													<span className='step-description'>
														Gehe zu „Gruppen" und klicke auf „Neue
														Gruppe erstellen".
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Gruppennamen eingeben
													</span>
													<span className='step-description'>
														Gib einen aussagekräftigen Namen für die
														Gruppe ein (z.B. „Haushalt", „Schule").
														Optional kannst du noch eine Beschreibung
														hinzufügen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Mitglieder hinzufügen
													</span>
													<span className='step-description'>
														Im nächsten Schritt kannst du die mit dir
														verknüpften Kinderprofil hinzufügen und
														deren Rollen innerhalb der Gruppe festlegen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Gruppe erstellen
													</span>
													<span className='step-description'>
														Klicke auf „Gruppe erstellen" und die Gruppe
														ist nun erstellt.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>

									<div className='guide-additional-info'>
										<h3>Rollen in Gruppen</h3>
										<ul>
											<li>
												<strong>Bearbeiter:</strong> Können nur Aufgaben
												erledigen und Belohnungen fordern. Sie können keine
												Aufgaben oder Belohnungen erstellen.
											</li>
											<li>
												<strong>Ersteller:</strong> Können Aufgaben und
												Belohnungen erstellen und verwalten. Sie können
												keine Aufgaben abschliessen oder Belohnungen
												einfordern.
											</li>
											<li>
												<strong>Verwalter</strong> Können alle Aktionen des
												Erstellers durchführen und zusätzlich noch die
												Gruppe verwalten.
											</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Andere Elternteile einladen */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Andere Elternteile einladen</h3>
										<p>
											Lade andere Elternteile in deine Gruppe ein, damit sie
											Aufgaben und Belohnungen verwalten können.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'group-invite'
													? null
													: 'group-invite'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'group-invite'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'group-invite' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'group-invite' ? 'auto' : 0,
											opacity: openAccordion === 'group-invite' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														In die Gruppe navigieren
													</span>
													<span className='step-description'>
														Öffne die Gruppe, in der du andere
														Elternteile einladen möchtest, navigiere
														dort zu Verwaltung.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Einladen auswählen
													</span>
													<span className='step-description'>
														Klicke auf „Einladen" und dann auf
														Einladungscode.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Rolle für Einladung festlegen
													</span>
													<span className='step-description'>
														Wähle die Rolle für die Einladung aus.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Code erstellen
													</span>
													<span className='step-description'>
														Generiere einen Einladungscode für die
														Gruppe.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>5</span>
												<div className='step-content'>
													<span className='step-title'>Code teilen</span>
													<span className='step-description'>
														Teile den Code mit dem anderen Elternteil.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>6</span>
												<div className='step-content'>
													<span className='step-title'>
														Anderer Elternteil: Gruppe beitreten
													</span>
													<span className='step-description'>
														Das andere Elternteil geht zur
														Gruppenübersicht und klickt auf „Gruppe
														beitreten".
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>7</span>
												<div className='step-content'>
													<span className='step-title'>
														Code eingeben
													</span>
													<span className='step-description'>
														Das andere Elternteil gibt den Code ein und
														bestätigt. Nun ist er Teil der Gruppe.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>
							<div className='guide-additional-info'>
								<h3>Hinweise zu Einladungscodes</h3>
								<ul>
									<li>
										Einladungscodes können nicht für Kinder verwendet werden.
									</li>
									<li>Einladungscodes laufen nach 15 min ab.</li>
									<li>
										Für jedes eingeladene Mitglied muss eine neue Einladung
										erstellt werden. Codes können nicht mehrfach verwendet
										werden.
									</li>
								</ul>
							</div>
						</motion.div>
					</section>

					{/* Tasks Guide Section */}
					<section className='deletion-guide-section' id='tasks'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.25 }}
							className='deletion-guide-container'
						>
							<h2>Aufgaben</h2>

							{/* Aufgaben erstellen (Ersteller) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Aufgaben erstellen (Ersteller)</h3>
										<p>
											Erstelle Aufgaben für deine Familie. Aufgaben helfen
											dir, Verantwortungen zu verteilen und die Zusammenarbeit
											zu fördern.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'tasks-create'
													? null
													: 'tasks-create'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'tasks-create'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'tasks-create' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'tasks-create' ? 'auto' : 0,
											opacity: openAccordion === 'tasks-create' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Zur Gruppe navigieren
													</span>
													<span className='step-description'>
														Öffne die Gruppe, in der du eine Aufgabe
														erstellen möchtest.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Neue Aufgabe erstellen
													</span>
													<span className='step-description'>
														Klicke auf „Aufgabe erstellen" oder das
														Plus-Symbol.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Aufgabendetails eingeben
													</span>
													<span className='step-description'>
														Gib mindestens einen Titel und die zu
														verdienenden Coins ein. Optional kannst du
														noch eine Beschreibung und ein
														Fälligkeitsdatum hinzufügen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Zuständigen zuweisen
													</span>
													<span className='step-description'>
														Wähle aus, welcher Bearbeiter die Aufgabe
														erledigen soll oder ob sie für alle
														verfügbar ist.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>5</span>
												<div className='step-content'>
													<span className='step-title'>
														Aufgabe speichern
													</span>
													<span className='step-description'>
														Speichere die Aufgabe und sie wird sofort
														angezeigt.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>

							{/* Aufgaben abschließen (Bearbeiter) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Aufgaben abschließen (Bearbeiter)</h3>
										<p>
											Als Bearbeiter kannst du zugewiesene Aufgaben erledigen
											und einreichen. Der Ersteller kann deine Arbeit dann
											überprüfen und bestätigen.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'tasks-complete'
													? null
													: 'tasks-complete'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'tasks-complete'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'tasks-complete'
															? 180
															: 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'tasks-complete' ? 'auto' : 0,
											opacity: openAccordion === 'tasks-complete' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Aufgabenübersicht
													</span>
													<span className='step-description'>
														Navigiere zur Aufgabenübersicht in der
														Gruppe.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Aufgabe auswählen
													</span>
													<span className='step-description'>
														Wähle die Aufgabe aus die du erledigen
														möchtest.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>Abschließen</span>
													<span className='step-description'>
														Drück auf "Aufgabe erledigen" in der
														Aufgabendetaillansicht.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Abschluss bestätigen
													</span>
													<span className='step-description'>
														Bestätige den Abschluss der Aufgabe.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>
							<div className='guide-additional-info'>
								<h3>Hinweise zum Abschließen von Aufgaben</h3>
								<ul>
									<li>
										Die Coins werden erst <strong>nach der Bestätigung</strong>{' '}
										des Erstellers vergeben, nicht nach dem Abschließen der
										Aufgabe.
									</li>
									<li>
										Wenn eine Aufgabe für mehrere Bearbeiter verfügbar ist,
										können mehrere Bearbeiter die Aufgabe als erledigt
										markieren.
									</li>
									<li>
										Jeder Bearbeiter kann eine Aufgabe nur einmal als erledigt
										markieren.
									</li>
								</ul>
							</div>

							{/* Aufgaben bestätigen/ablehnen (Ersteller) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Aufgaben bestätigen/ablehnen (Ersteller)</h3>
										<p>
											Als Ersteller kannst du eingereichte Aufgaben überprüfen
											und bestätigen oder ablehnen. Du kannst auch Kommentare
											hinterlassen.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'tasks-review'
													? null
													: 'tasks-review'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'tasks-review'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'tasks-review' ? 180 : 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'tasks-review' ? 'auto' : 0,
											opacity: openAccordion === 'tasks-review' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Eingereichte Aufgabe öffnen
													</span>
													<span className='step-description'>
														Öffne die Aufgabe, die zur Überprüfung
														eingereicht wurde.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Arbeit überprüfen
													</span>
													<span className='step-description'>
														Überprüfe ob die Aufgabe die dort
														beschrieben wurde auch wirklich erledigt
														wurde.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Kommentar hinterlassen (optional)
													</span>
													<span className='step-description'>
														Du kannst einen Kommentar hinterlassen, um
														Feedback zu geben oder weitere Informationen
														zu fordern.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Bestätigen oder ablehnen
													</span>
													<span className='step-description'>
														Klicke auf „Bestätigen" um die Aufgabe zu
														akzeptieren und Coins zu vergeben, oder auf
														„Ablehnen" um sie zurückzuweisen.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>
							<div className='guide-additional-info'>
								<h3>Hinweise zum Bestätigen von Aufgaben</h3>
								<ul>
									<li>
										Die Coins werden erst <strong>nach der Bestätigung</strong>{' '}
										des Erstellers vergeben, nicht nach dem Abschließen der
										Aufgabe.
									</li>
									<li>
										Eine abgelehnte Aufgabe kann nochmal versucht werden, ein
										Ersteller der Gruppe muss die Aufgabe erneut prüfen.
									</li>
									<li>Es ist möglich mehrere Abschlüsse anzunehmen.</li>
								</ul>
							</div>
						</motion.div>
					</section>

					{/* Rewards Guide Section */}
					<section className='deletion-guide-section' id='rewards'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
							className='deletion-guide-container'
						>
							<h2>Belohnungen</h2>

							{/* Belohnungen erstellen (Ersteller) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Belohnungen erstellen (Ersteller)</h3>
										<p>
											Erstelle Belohnungen, die Kinder mit ihren Coins kaufen
											können. Belohnungen motivieren Kinder, Aufgaben zu
											erledigen und gutes Verhalten zu zeigen.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'rewards-create'
													? null
													: 'rewards-create'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'rewards-create'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'rewards-create'
															? 180
															: 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'rewards-create' ? 'auto' : 0,
											opacity: openAccordion === 'rewards-create' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Zur Gruppe navigieren
													</span>
													<span className='step-description'>
														Öffne die Gruppe, in der du eine Belohnung
														erstellen möchtest und navigiere zu
														Belohnungen.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Neue Belohnung erstellen
													</span>
													<span className='step-description'>
														Klicke auf „Belohnung erstellen" oder das
														Plus-Symbol im Belohnungsbereich.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnungsdetails eingeben
													</span>
													<span className='step-description'>
														Gib mindestens den Titel und die zu
														vergebenden Coins ein. Optional kannst du
														noch eine Beschreibung hinzufügen, die
														Sichtbarkeit der Belohnung und ob sie
														Wiederkehrend ist.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnung speichern
													</span>
													<span className='step-description'>
														Speichere die Belohnung und sie wird sofort
														für Kinder verfügbar.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>

							{/* Belohnungen fordern (Bearbeiter) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Belohnungen fordern (Bearbeiter)</h3>
										<p>
											Als Bearbeiter kannst du Belohnungen mit deinen Coins
											kaufen. Der Ersteller muss deine Anfrage dann bestätigen
											oder ablehnen.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'rewards-request'
													? null
													: 'rewards-request'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'rewards-request'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'rewards-request'
															? 180
															: 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height:
												openAccordion === 'rewards-request' ? 'auto' : 0,
											opacity: openAccordion === 'rewards-request' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnungen anschauen
													</span>
													<span className='step-description'>
														Öffne die Gruppe und schaue dir die
														verfügbaren Belohnungen an.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnung auswählen
													</span>
													<span className='step-description'>
														Wähle eine Belohnung aus, die du mit deinen
														Coins kaufen möchtest.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnung fordern
													</span>
													<span className='step-description'>
														Klicke auf „Belohnung fordern" oder
														„Kaufen".
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Auf Bestätigung warten
													</span>
													<span className='step-description'>
														Der Ersteller überprüft deine Anfrage und
														bestätigt oder lehnt sie ab.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>

							{/* Belohnungen bestätigen/ablehnen (Ersteller) */}
							<div className='guide-subsection'>
								<div className='guide-content'>
									<div className='guide-text'>
										<h3>Belohnungen bestätigen/ablehnen (Ersteller)</h3>
										<p>
											Als Ersteller kannst du Belohnungsanfragen überprüfen
											und bestätigen oder ablehnen. Du kannst auch Kommentare
											hinterlassen.
										</p>
									</div>

									<motion.button
										className='accordion-button'
										onClick={() =>
											setOpenAccordion(
												openAccordion === 'rewards-review'
													? null
													: 'rewards-review'
											)
										}
										whileHover={{ backgroundColor: '#E8E6FF' }}
										whileTap={{ scale: 0.98 }}
									>
										<div className='accordion-header'>
											<div className='accordion-title'>
												<span>
													{openAccordion === 'rewards-review'
														? 'Schritte ausblenden'
														: 'Schritte anzeigen'}
												</span>
											</div>
											<motion.div
												animate={{
													rotate:
														openAccordion === 'rewards-review'
															? 180
															: 0,
												}}
												transition={{ duration: 0.3 }}
												className='chevron-icon'
											>
												<FaChevronDownIcon />
											</motion.div>
										</div>
									</motion.button>

									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{
											height: openAccordion === 'rewards-review' ? 'auto' : 0,
											opacity: openAccordion === 'rewards-review' ? 1 : 0,
										}}
										transition={{ duration: 0.3, ease: 'easeInOut' }}
										className='accordion-content'
									>
										<ol className='steps-list'>
											<li>
												<span className='step-number'>1</span>
												<div className='step-content'>
													<span className='step-title'>
														Belohnungsanfrage öffnen
													</span>
													<span className='step-description'>
														Öffne die Belohnungsanfrage, die zur
														Überprüfung eingereicht wurde.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>2</span>
												<div className='step-content'>
													<span className='step-title'>
														Anfrage überprüfen
													</span>
													<span className='step-description'>
														Überprüfe die Anfrage und stelle sicher dass
														du die Belohnung bereitstellen kannst.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>3</span>
												<div className='step-content'>
													<span className='step-title'>
														Kommentar hinterlassen (optional)
													</span>
													<span className='step-description'>
														Du kannst einen Kommentar hinterlassen, um
														Feedback zu geben oder weitere Informationen
														zu fordern.
													</span>
												</div>
											</li>
											<li>
												<span className='step-number'>4</span>
												<div className='step-content'>
													<span className='step-title'>
														Bestätigen oder ablehnen
													</span>
													<span className='step-description'>
														Klicke auf „Bestätigen" um die Belohnung zu
														gewähren, oder auf „Ablehnen" um sie
														zurückzuweisen.
													</span>
												</div>
											</li>
										</ol>
									</motion.div>
								</div>
							</div>
						</motion.div>
					</section>

					{/* Account Deletion Guide Section */}
					<section className='deletion-guide-section' id='delete-account'>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
							className='deletion-guide-container'
						>
							<h2>Anleitung zum Löschen des Erledix Accounts</h2>

							<div className='guide-content'>
								<div className='guide-text'>
									<h3>Schritt-für-Schritt Anleitung</h3>
									<p>
										{' '}
										Wenn du deinen Erledix Account löschen möchtest, folge
										einfach den Schritten unten. Bitte beachte, dass diese
										Aktion nicht rückgängig gemacht werden kann
									</p>
								</div>
								<div className='deletion-warning-box'>
									<p>
										⚠️ <strong>Wichtig:</strong> Alle verbundenen Daten des
										Nutzers werden damit unwiderruflich gelöscht. Dies umfasst
										alle Aufgaben, Belohnungen, Coins, Kommentare und
										Profilinformationen.
									</p>
								</div>

								<motion.button
									className='accordion-button'
									onClick={() => setIsAccordionOpen(!isAccordionOpen)}
									whileHover={{ backgroundColor: '#E8E6FF' }}
									whileTap={{ scale: 0.98 }}
								>
									<div className='accordion-header'>
										<div className='accordion-title'>
											<FaTrashIcon className='accordion-icon' />
											<span>
												{isAccordionOpen
													? 'Schritte ausblenden'
													: 'Schritte anzeigen'}
											</span>
										</div>
										<motion.div
											animate={{ rotate: isAccordionOpen ? 180 : 0 }}
											transition={{ duration: 0.3 }}
											className='chevron-icon'
										>
											<FaChevronDownIcon />
										</motion.div>
									</div>
								</motion.button>

								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{
										height: isAccordionOpen ? 'auto' : 0,
										opacity: isAccordionOpen ? 1 : 0,
									}}
									transition={{ duration: 0.3, ease: 'easeInOut' }}
									className='accordion-content'
								>
									<ol className='steps-list'>
										<li>
											<span className='step-number'>1</span>
											<div className='step-content'>
												<span className='step-title'>Anmelden</span>
												<span className='step-description'>
													Öffne die Erledix App und melde dich mit deinen
													Anmeldedaten an.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>2</span>
											<div className='step-content'>
												<span className='step-title'>
													Beim Elternprofil einloggen
												</span>
												<span className='step-description'>
													Stelle sicher, dass du mit dem Elternprofil
													angemeldet bist, da nur Eltern Konten löschen
													können.
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>3</span>
											<div className='step-content'>
												<span className='step-title'>
													Zu den Profileinstellungen navigieren
												</span>
												<span className='step-description'>
													Gehe zu den Einstellungen und wähle
													„Profileinstellungen" oder „Mein Profil".
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>4</span>
											<div className='step-content'>
												<span className='step-title'>
													Konto löschen auswählen
												</span>
												<span className='step-description'>
													Scrolle nach unten und klicke auf die Option
													„Konto löschen".
												</span>
											</div>
										</li>
										<li>
											<span className='step-number'>5</span>
											<div className='step-content'>
												<span className='step-title'>
													Löschung bestätigen
												</span>
												<span className='step-description'>
													Bestätige die Löschung in dem
													Bestätigungsdialog. Dein Account wird dann
													sofort gelöscht.
												</span>
											</div>
										</li>
									</ol>
								</motion.div>

								<div className='guide-additional-info'>
									<h3>Weitere Informationen</h3>
									<ul>
										<li>
											<strong>Datenlöschung:</strong> Alle deine Daten werden
											innerhalb von 30 Tagen vollständig von unseren Servern
											gelöscht.
										</li>
										<li>
											<strong>Keine Wiederherstellung:</strong> Gelöschte
											Accounts können nicht wiederhergestellt werden.
										</li>
										<li>
											<strong>Familiengruppen:</strong> Wenn du der
											Gruppenleiter bist, wird der Verwalter zum
											Gruppenbesitzer der am längsten in der Gruppe ist.
											Sollten es keine Verwalter in deiner Gruppe geben, wird
											die gesamte Gruppe gelöscht.
										</li>
										<li>
											<strong>Kinderprofile:</strong> Alle deine
											Elternverknüpfungen werden gelöscht. Wenn keine weiteren
											Elternverknüpfungen bei einem Kinderprofil vorhanden
											sind, wird das Kinderprofil ebenfalls gelöscht.
										</li>
										<li>
											<strong>Hilfe benötigt?</strong> Wenn du Fragen hast,
											kontaktiere uns unter support@erledix.de
										</li>
									</ul>
								</div>
							</div>
						</motion.div>
					</section>
				</div>
			</div>
			<MobileFooter />
		</div>
	);
};
